import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ToggleLeft,
  ToggleRight,
  UserPlus,
  Users,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import AIUsagePanel from '../components/AIUsagePanel';
import ProviderHealthPanel from '../components/ProviderHealthPanel';
import QuoteInbox from '../components/QuoteInbox';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { RegisterForm } from './Auth';

const inputCls = "w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all";
const labelCls = "text-xs font-medium text-zinc-700 mb-1.5 block uppercase tracking-wider";

function Notice({ notice }) {
  if (!notice?.text) return null;
  const isError = notice.type === 'error';
  return (
    <div className={`border p-3 text-sm flex items-start gap-2 ${isError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`} data-testid="admin-notice">
      {isError ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
      <span>{notice.text}</span>
    </div>
  );
}

function FieldErrors({ messages }) {
  if (!messages?.length) return null;
  return (
    <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex gap-2" data-testid="admin-form-errors">
      <AlertCircle className="w-4 h-4 mt-px shrink-0" />
      <ul className="space-y-1">{messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
    </div>
  );
}

function ToggleRow({ title, description, enabled, disabled, onToggle, testid }) {
  return (
    <div className="flex flex-col gap-4 border border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-display text-lg font-semibold text-zinc-950">{title}</div>
        <p className="mt-1 text-sm text-zinc-600">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onToggle(!enabled)}
        className={`inline-flex h-11 min-w-[8.5rem] items-center justify-center gap-2 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? 'bg-[#002FA7] text-white hover:bg-[#00227A]' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}
        data-testid={testid}
      >
        {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
        {enabled ? 'On' : 'Off'}
      </button>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSetting, setSavingSetting] = useState('');
  const [notice, setNotice] = useState(null);
  const [passwordEmail, setPasswordEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const pilotUsers = useMemo(() => users.filter((item) => !item.is_admin), [users]);

  const loadUsers = async () => {
    const usersRes = await api.get('/admin/users');
    setUsers(usersRes.data.users || []);
  };

  const loadAdminData = async () => {
    setLoading(true);
    setNotice(null);
    try {
      const [settingsRes, usersRes, questionsRes] = await Promise.all([
        api.get('/admin/settings'),
        api.get('/admin/users'),
        api.get('/auth/security-questions'),
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data.users || []);
      setQuestions(questionsRes.data.questions || []);
    } catch (err) {
      if (err?.response?.status === 403) nav('/assessment');
      else setNotice({ type: 'error', text: err?.response?.data?.detail || 'Unable to load admin settings.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hasToken = !!localStorage.getItem('startin_token');
    if (!user && !hasToken) nav('/auth');
    if (user && !user.is_admin) nav('/assessment');
    if (user?.is_admin) loadAdminData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!passwordEmail && pilotUsers.length) setPasswordEmail(pilotUsers[0].email);
  }, [passwordEmail, pilotUsers]);

  const updateSetting = async (key, value) => {
    setSavingSetting(key);
    setNotice(null);
    try {
      const res = await api.put('/admin/settings', { [key]: value });
      setSettings(res.data);
      setNotice({ type: 'success', text: 'Access setting updated.' });
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.detail || 'Unable to update setting.' });
    } finally {
      setSavingSetting('');
    }
  };

  const createPilotUser = async (payload) => {
    const res = await api.post('/admin/users', payload);
    return res.data.user;
  };

  const onCreatePilotSuccess = async (createdUser) => {
    setNotice({ type: 'success', text: `${createdUser.email} was created.` });
    await loadUsers();
    setPasswordEmail(createdUser.email);
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setPasswordErrors([]);
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setPasswordErrors(['Passwords do not match.']);
      return;
    }
    setPasswordSaving(true);
    try {
      await api.post('/admin/users/password', { email: passwordEmail, new_password: newPassword });
      setNewPassword('');
      setConfirmPassword('');
      setNotice({ type: 'success', text: `Password updated for ${passwordEmail}.` });
      await loadUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPasswordErrors(Array.isArray(detail) ? detail : [detail || 'Unable to update password.']);
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="mx-auto max-w-[1400px] px-6 py-12 text-sm text-zinc-500 lg:px-10">Loading admin access...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
        <div className="mb-8 border-b border-zinc-200 pb-6">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">/ Admin · Access</div>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight text-zinc-950">Admin Control</h1>
              <p className="mt-2 max-w-2xl text-sm text-zinc-600">Manage pilot user access, password resets, and the public release switches.</p>
            </div>
            <button
              type="button"
              onClick={loadAdminData}
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 bg-zinc-950 px-4 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-60"
              data-testid="admin-refresh"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-6">
          <Notice notice={notice} />
        </div>

        {loading && (
          <div className="border border-zinc-200 p-8 text-sm text-zinc-500" data-testid="admin-loading">
            Loading admin settings...
          </div>
        )}

        {!loading && settings && (
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(24rem,0.75fr)]">
            <section className="space-y-8">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-2xl font-bold text-zinc-950">Release Switches</h2>
                </div>
                <div className="space-y-3">
                  <ToggleRow
                    title="Public registration"
                    description="When off, only admins can create new non-admin login IDs."
                    enabled={settings.registration_enabled}
                    disabled={savingSetting === 'registration_enabled'}
                    onToggle={(value) => updateSetting('registration_enabled', value)}
                    testid="toggle-registration"
                  />
                  <ToggleRow
                    title="Forgot password"
                    description="When off, non-admin users cannot self-reset passwords from the sign-in screen."
                    enabled={settings.forgot_password_enabled}
                    disabled={savingSetting === 'forgot_password_enabled'}
                    onToggle={(value) => updateSetting('forgot_password_enabled', value)}
                    testid="toggle-forgot-password"
                  />
                  <ToggleRow
                    title="Live GST validation"
                    description="When on, new users must have an active GST number whose registered legal name matches their company name."
                    enabled={settings.gst_live_validation_enabled}
                    disabled={savingSetting === 'gst_live_validation_enabled'}
                    onToggle={(value) => updateSetting('gst_live_validation_enabled', value)}
                    testid="toggle-gst-live-validation"
                  />
                </div>
              </div>

              <div>
                <div className="mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-2xl font-bold text-zinc-950">Users</h2>
                </div>
                <div className="overflow-x-auto border border-zinc-200">
                  <table className="w-full min-w-[42rem] text-left text-sm" data-testid="admin-users-table">
                    <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      <tr>
                        <th className="px-4 py-3 font-medium">Company</th>
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">Role</th>
                        <th className="px-4 py-3 font-medium">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200">
                      {users.map((item) => (
                        <tr key={item.id} className="bg-white">
                          <td className="px-4 py-3 font-medium text-zinc-950">{item.company_name}</td>
                          <td className="px-4 py-3 text-zinc-600">{item.email}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-[11px] uppercase tracking-wider ${item.is_admin ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-zinc-100 text-zinc-600'}`}>
                              {item.is_admin ? 'Admin' : 'Pilot'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-zinc-500">{item.updated_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <aside className="space-y-8">
              <section>
                <div className="mb-4 flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-2xl font-bold text-zinc-950">Create Pilot User</h2>
                </div>
                <div className="border border-zinc-200 p-5">
                  <RegisterForm
                    onSubmit={createPilotUser}
                    onSuccess={onCreatePilotSuccess}
                    questions={questions}
                    submitLabel="Create login ID"
                    resetOnSuccess
                  />
                </div>
              </section>

              <section>
                <div className="mb-4 flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-[#002FA7]" />
                  <h2 className="font-display text-2xl font-bold text-zinc-950">Update Password</h2>
                </div>
                <form onSubmit={updatePassword} className="space-y-4 border border-zinc-200 p-5" data-testid="admin-password-form">
                  <div>
                    <label className={labelCls}>Pilot user</label>
                    <select
                      className={inputCls}
                      value={passwordEmail}
                      onChange={(e) => setPasswordEmail(e.target.value)}
                      required
                      data-testid="admin-password-email"
                    >
                      {pilotUsers.length === 0 && <option value="">No pilot users</option>}
                      {pilotUsers.map((item) => <option key={item.id} value={item.email}>{item.email}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>New password</label>
                      <input className={inputCls} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="admin-new-password" />
                    </div>
                    <div>
                      <label className={labelCls}>Confirm</label>
                      <input className={inputCls} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="admin-confirm-password" />
                    </div>
                  </div>
                  <FieldErrors messages={passwordErrors} />
                  <button
                    type="submit"
                    disabled={passwordSaving || !passwordEmail}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 bg-[#002FA7] px-4 text-sm font-medium text-white transition-colors hover:bg-[#00227A] disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="admin-password-submit"
                  >
                    {passwordSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                    Update password
                  </button>
                </form>
              </section>
            </aside>
          </div>
        )}
        {user?.is_admin && <QuoteInbox />}
        {user?.is_admin && <ProviderHealthPanel />}
        {user?.is_admin && <AIUsagePanel />}
      </main>
    </div>
  );
}
